import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { FiEdit, FiTrash2, FiRepeat, FiRotateCcw } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  DetailTabBar, DetailRow, DetailStatusPill, ApproverCard, ImageBlock, FeedbackTab, ConfirmModal,
} from "../../components/financeDetail";
import { getBookingByIdApi, deleteBookingApi, refundBookingApi } from "../../api/finance.booking.api";
import { extractErrorMessage } from "../../api/client";
import { formatMoney } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const TEXT = "#08142D";
const MUTED = "#94A3B8";
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const BookingDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [showDelete, setShowDelete] = useState(false);
  const [showRefund, setShowRefund] = useState(false);

  const load = () => {
    setLoading(true);
    getBookingByIdApi(id).then((d) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const tabs = useMemo(() => {
    const r = [{ key: "overview", title: "Tổng quan" }];
    if (data?.images?.length) r.push({ key: "transaction", title: "Giao dịch" });
    if (data?._id) r.push({ key: "interactNote", title: "Phản hồi" });
    return r;
  }, [data]);
  useEffect(() => { if (tab >= tabs.length) setTab(0); }, [tabs.length, tab]);
  const activeKey = tabs[tab]?.key || "overview";

  const st = data?.status;
  const isNew = st === "NEW";
  const isApproved = st === "APPROVED";
  const canRefund = st && st !== "NEW" && st !== "PENDINGRETURNREQUEST" && st !== "REFUND";

  const doDelete = async () => {
    setShowDelete(false);
    try { await deleteBookingApi(id); toast.success("Xóa booking thành công"); navigate(`${EMP_BASE_PATH}/tai-chinh/booking`); }
    catch (e) { toast.error(extractErrorMessage(e, "Xóa booking thất bại")); }
  };
  const doRefund = async () => {
    setShowRefund(false);
    try { await refundBookingApi(id); toast.success("Gửi yêu cầu hoàn booking thành công"); load(); }
    catch (e) { toast.error(extractErrorMessage(e, "Hoàn booking thất bại")); }
  };
  const goLocking = () => {
    const q = new URLSearchParams({ mode: "locking" });
    if (data?._id) q.set("bookingId", data._id);
    if (data?.apartment?._id) { q.set("apartmentId", data.apartment._id); q.set("code", data.apartment.code || ""); }
    if (data?.project?._id) q.set("projectId", data.project._id);
    navigate(`${EMP_BASE_PATH}/tai-chinh/locking/tao?${q.toString()}`);
  };

  const outlineBtn = { variant: "outline", borderColor: "#DCE1EA", color: TEXT, borderRadius: "8px", h: "48px", _hover: { bg: "#F7F9FC" } };

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : !data ? (
        <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy thông tin booking</Text></Flex>
      ) : (
        <>
          <DetailTabBar tabs={tabs} index={tab} onChange={setTab} />

          <Box flex="1" overflowY="auto" px="16px" pt="16px">
            {activeKey === "overview" && (
              <Box pb="20px">
                <Text fontSize="16px" fontWeight={600} color={TEXT} noOfLines={2}>
                  {data?.project?.name || ""}{data?.project?.name ? " - " : ""}{data?.apartment?.code || "Không định danh"}
                </Text>
                <Box mt="12px"><DetailStatusPill status={st} /></Box>
                <ApproverCard approver={data?.bookingHistory?.approver} dateLabel={fmtD(data?.createdAt)} />

                <Box mt="8px">
                  <DetailRow label="Dự án" value={data?.project?.name} onPress={data?.projectId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an/${data.projectId}`) : undefined} />
                  <DetailRow label="Căn hộ" value={data?.apartment?.code ? `${data.apartment.code} (${data?.apartment?.name || ""})` : "-"} />
                  <DetailRow label="Giá trị niêm yết" value={formatMoney(data?.apartment?.totalValue || 0)} />
                  <DetailRow label="Khách hàng" value={data?.customer?.name} onPress={data?.customerId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/${data.customerId}`) : undefined} />
                  <DetailRow label="Ngày vào cọc" value={fmtD(data?.bookingTime)} />
                  <DetailRow label="Ngày tạo booking" value={fmtD(data?.createdAt)} />
                  <DetailRow label="Số tiền đặt cọc" value={formatMoney(data?.price || 0)} color={APP.primary200} />
                  <DetailRow label="Ghi chú" value={data?.note} last />
                </Box>
              </Box>
            )}

            {activeKey === "transaction" && (
              <Box pb="20px"><ImageBlock images={data?.images} emptyText="Chưa có ảnh ủy nhiệm chi" /></Box>
            )}

            {activeKey === "interactNote" && (
              <Box pb="20px"><FeedbackTab query={{ bookingId: id }} /></Box>
            )}
          </Box>

          {tab === 0 && (isNew || canRefund) && (
            <Box borderTop="1px solid #EEF1F6" p="16px">
              {isNew && (
                <Flex gap="16px">
                  <Button flex="1" {...outlineBtn} leftIcon={<FiEdit />} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/booking/tao?id=${id}`)}>Sửa</Button>
                  <Button flex="1" {...outlineBtn} leftIcon={<FiTrash2 />} onClick={() => setShowDelete(true)}>Xóa</Button>
                </Flex>
              )}
              {canRefund && (
                <>
                  {isApproved && (
                    <Button w="100%" {...outlineBtn} mb="16px" onClick={goLocking}>Lên locking</Button>
                  )}
                  <Flex gap="16px">
                    <Button flex="1" {...outlineBtn} leftIcon={<FiRotateCcw />} onClick={() => setShowRefund(true)}>Hoàn booking</Button>
                    {isApproved && (
                      <Button flex="1" {...outlineBtn} leftIcon={<FiRepeat />} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/booking/chuyen/${id}`)}>Chuyển booking</Button>
                    )}
                  </Flex>
                </>
              )}
            </Box>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={showDelete}
        title="Xóa phiếu"
        message={`Bạn có chắc chắn muốn xóa booking ${data?.apartment?.name || data?.code || ""}?`}
        confirmText="Xóa"
        onConfirm={doDelete}
        onClose={() => setShowDelete(false)}
      />
      <ConfirmModal
        isOpen={showRefund}
        title="Hoàn booking"
        message={`Bạn có chắc chắn muốn hoàn booking ${data?.apartment?.name || data?.code || ""}?`}
        confirmText="Hoàn trả"
        onConfirm={doRefund}
        onClose={() => setShowRefund(false)}
      />
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết đơn đăng ký booking" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Chi tiết đơn đăng ký booking" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/booking`)} />
      {pageBody}
    </Box>
  );
};

export default BookingDetail;
